import { get, post } from "./api";
import type {
  InsertScriptureReference,
  InsertScriptureNote,
  ScriptureReference,
  ScriptureNote,
} from "@shared/types/supabase";
import bibleBooks from "../data/bible_books.json";

export interface BibleSearchResult {
  reference: string;
  text: string;
  translation: string;
  bibleId?: number;
  usfm?: string;
  book?: string;
  chapter?: number;
  verse_start?: number;
  verse_end?: number;
}

export interface BibleVersionOption {
  id: number;
  abbreviation: string;
  title: string;
}

export const DEFAULT_TRANSLATION = "NIV";

export async function searchBibleVerse(
  reference: string,
  translation: string = DEFAULT_TRANSLATION,
  bibleId?: number,
): Promise<BibleSearchResult> {
  const params = new URLSearchParams({
    reference,
    translation,
  });
  if (bibleId != null) params.set("bibleId", String(bibleId));
  return await get<BibleSearchResult>(`/bible/verse?${params.toString()}`);
}

export async function searchBibleByQuery(
  query: string,
  translation: string = DEFAULT_TRANSLATION,
): Promise<BibleSearchResult[]> {
  return await get<BibleSearchResult[]>(
    `/bible/search?q=${encodeURIComponent(query)}&translation=${encodeURIComponent(translation)}`,
  );
}

export async function listBibleVersions(): Promise<{
  default: string;
  versions: BibleVersionOption[];
}> {
  return await get("/bible/versions");
}

export async function createScriptureReference(
  scriptureRef: InsertScriptureReference,
): Promise<{ id: number }> {
  return await post<{ id: number }>("/scripture-references", scriptureRef);
}

export async function getScriptureReferences(): Promise<ScriptureReference[]> {
  return await get<ScriptureReference[]>("/scripture-references");
}

export async function createScriptureNote(
  note: InsertScriptureNote,
): Promise<{ id: number }> {
  const parentId = note.parent_id ?? note.scripture_reference_id;
  if (parentId == null) {
    throw new Error("parent_id (scripture reference id) is required");
  }
  const result = await post<{ note: { id: number } }>("/notes", {
    content: note.content,
    parent_type: "scripture",
    parent_id: parentId,
  });
  return { id: result.note.id };
}

export async function getScriptureNotes(
  _documentId?: number,
): Promise<ScriptureNote[]> {
  return await get<ScriptureNote[]>("/scripture-notes");
}

export interface BibleBook {
  Long: string;
  Short: string;
  Shorter_Single?: string;
  no_of_chapters: number;
  chapters_to_number_of_verses: number[];
}

/** Local book metadata for pickers (chapter/verse counts) — not YouVersion text. */
export async function loadBibleData(): Promise<Record<string, BibleBook>> {
  // Imported into the bundle so chapter/verse pickers work without a network
  // connection in the Mac app. The old absolute `/src/...` fetch only existed
  // on the Vite development server.
  return bibleBooks as Record<string, BibleBook>;
}
